import { useState, useEffect } from 'react';
import { Check, KeyboardArrowDown } from '@mui/icons-material';
import { Box, Menu, MenuItem, Stack } from '@mui/material';
import Button from 'domains/core/components/Button';
import { ContentStatus } from '../ContentTable/ContentStatusText';
import { themeColors } from 'colors';

type StatusFilterDropdownProps = {
    selectedStatuses: ContentStatus[];
    onApply: (statuses: ContentStatus[]) => void;
};

const statusOptions = [
    { value: ContentStatus.DRAFT, label: 'Draft' },
    { value: ContentStatus.PUBLISHED, label: 'Published' },
    { value: ContentStatus.LIVE, label: 'Live' },
];

const StatusFilterDropdown = ({ selectedStatuses, onApply }: StatusFilterDropdownProps) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [localSelectedStatuses, setLocalSelectedStatuses] = useState<ContentStatus[]>(selectedStatuses);
    const isOpen = Boolean(anchorEl);

    // Update local state when selectedStatuses prop changes
    useEffect(() => {
        setLocalSelectedStatuses(selectedStatuses);
    }, [selectedStatuses]);

    // Opens the status filter dropdown menu when the Status button is clicked
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    // Closes the status filter dropdown menu
    const handleClose = () => {
        setAnchorEl(null);
    };

    // Toggles the selection of a status option (selects if unselected, deselects if already selected)
    // Updates only local state - changes are not applied until Apply button is clicked
    const handleToggleStatus = (status: ContentStatus) => {
        setLocalSelectedStatuses((prev) =>
            prev.includes(status) ? prev.filter((s) => s !== status) : [...prev, status]
        );
    };

    // Applies the selected status filters to the URL and triggers API call
    // Closes the dropdown after applying
    const handleApply = () => {
        onApply(localSelectedStatuses);
        handleClose();
    };

    // Clears all selected status filters from local state
    // Does not apply changes - user must click Apply to update the filter
    const handleClear = () => {
        setLocalSelectedStatuses([]);
    };

    const hasSelectedStatuses = localSelectedStatuses.length > 0;

    return (
        <>
            <Button
                onClick={handleClick}
                endIcon={<KeyboardArrowDown />}
                sx={{
                    textTransform: 'capitalize',
                    color: hasSelectedStatuses ? themeColors.action.active : 'inherit',
                }}
                variant="text"
                aria-controls={isOpen ? 'status-filter-menu' : undefined}
                aria-expanded={isOpen ? 'true' : undefined}
                aria-haspopup="menu"
                data-testid="status-filter-button"
                role="button"
            >
                Status
            </Button>
            <Menu
                id="status-filter-menu"
                anchorEl={anchorEl}
                open={isOpen}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                PaperProps={{
                    sx: {
                        minWidth: '200px',
                        mt: 1,
                    },
                }}
                MenuListProps={{
                    role: 'menu',
                }}
            >
                <Box data-testid="status-filter-menu" sx={{ display: 'contents' }}>
                    {statusOptions.map((option) => {
                        const isSelected = localSelectedStatuses.includes(option.value);
                        return (
                            <MenuItem
                                key={option.value}
                                onClick={() => handleToggleStatus(option.value)}
                                selected={isSelected}
                                data-testid={`status-filter-option-${option.value.toLowerCase()}`}
                                role="menuitemcheckbox"
                                aria-checked={isSelected}
                                sx={{
                                    textTransform: 'capitalize',
                                    '&.Mui-selected': {
                                        backgroundColor: 'white',
                                        color: 'action.active',
                                    },
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                }}
                            >
                                {option.label}
                                {isSelected && (
                                    <Check
                                        data-testid={`status-filter-check-${option.value.toLowerCase()}`}
                                        sx={{
                                            color: '#00845B',
                                            fontSize: '20px',
                                            ml: 2,
                                        }}
                                    />
                                )}
                            </MenuItem>
                        );
                    })}
                    <Box
                        sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 1, mt: 0.5 }}
                        data-testid="status-filter-actions"
                    >
                        <Stack direction="row" spacing={1} sx={{ px: 2, pb: 1 }}>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleClear}
                                data-testid="status-filter-clear-button"
                                role="button"
                                sx={{
                                    flex: 1,
                                    textTransform: 'none',
                                }}
                                disabled={localSelectedStatuses.length === 0}
                            >
                                Clear
                            </Button>
                            <Button
                                variant="contained"
                                size="small"
                                onClick={handleApply}
                                data-testid="status-filter-apply-button"
                                role="button"
                                sx={{
                                    flex: 1,
                                    textTransform: 'none',
                                    bgcolor: '#00845B',
                                    '&:hover': {
                                        bgcolor: '#006B47',
                                    },
                                }}
                            >
                                Apply
                            </Button>
                        </Stack>
                    </Box>
                </Box>
            </Menu>
        </>
    );
};

export default StatusFilterDropdown;
